import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { COCKTAILS, COCKTAIL_FILTERS } from "../data/cocktails";
import PogLogo from "../components/PogLogo";

const CANONICAL = "https://pogspirits.com";

const FilterRow = ({ label, options, active, onChange, group }) => (
  <div className="flex flex-wrap items-center gap-3" data-testid={`filter-row-${group}`}>
    <span className="overline text-[10px] text-[#EFE7D6]/40 w-24 shrink-0">{label}</span>
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        data-testid={`filter-${group}-${o.key.toString().toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => onChange(o.key)}
        aria-pressed={active === o.key}
        className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors duration-300 ${
          active === o.key
            ? "bg-[#F26B1F] border-[#F26B1F] text-[#14100D] font-semibold"
            : "border-[#EFE7D6]/20 text-[#EFE7D6]/70 hover:border-[#F26B1F] hover:text-[#F26B1F]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const CocktailsGallery = () => {
  const [base, setBase] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [occasion, setOccasion] = useState("all");

  const filtered = useMemo(
    () =>
      COCKTAILS.filter(
        (c) =>
          (base === "all" || c.baseKey === base) &&
          (difficulty === "all" || c.difficulty === difficulty) &&
          (occasion === "all" || c.occasion === occasion)
      ),
    [base, difficulty, occasion]
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${CANONICAL}/cocktails`,
    name: "POG Spirits Cocktail Gallery",
    description:
      "Six signature POG Spirits cocktail recipes — filterable by base spirit, difficulty, and occasion. Built with POG Vodka and POG Spiked Island Punch.",
    url: `${CANONICAL}/cocktails`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: COCKTAILS.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${CANONICAL}/cocktails/${c.slug}`,
        name: c.name,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-[#14100D] text-[#EFE7D6]" data-testid="cocktail-gallery">
      <Helmet>
        <title>Cocktail Gallery — POG Spirits Recipes</title>
        <meta
          name="description"
          content="Six signature POG Spirits cocktails — from The Kailua Sunset to Pele's Punch Bowl. Filter by base spirit, difficulty, and occasion. Every recipe shareable."
        />
        <link rel="canonical" href={`${CANONICAL}/cocktails`} />
        <meta property="og:title" content="Cocktail Gallery — POG Spirits Recipes" />
        <meta
          property="og:description"
          content="Six signature Hawaiian cocktails built with POG Vodka and POG Spiked Island Punch. Filter, mix, share."
        />
        <meta property="og:url" content={`${CANONICAL}/cocktails`} />
        <meta property="og:image" content={`${CANONICAL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="fixed top-0 left-0 right-0 z-50 bg-[#14100D]/90 backdrop-blur-xl border-b border-[#EFE7D6]/10">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="gallery-logo-home">
            <PogLogo size={28} />
            <span className="font-body italic text-[#EFE7D6] tracking-wide">Spirits</span>
          </Link>
          <div className="flex items-center gap-8">
            <Link
              to="/journal"
              className="font-body text-[11px] tracking-[0.28em] uppercase text-[#EFE7D6]/75 hover:text-[#E85A15] transition-colors link-underline"
              data-testid="gallery-journal-link"
            >
              Journal
            </Link>
            <Link
              to="/"
              className="font-body text-[11px] tracking-[0.28em] uppercase text-[#EFE7D6]/75 hover:text-[#E85A15] transition-colors link-underline"
              data-testid="gallery-back-home"
            >
              Back to site
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-40 pb-32 px-8 md:px-12 max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="mb-16"
        >
          <div className="flex items-center gap-6 mb-8">
            <span className="counter">07 — The Gallery</span>
            <span className="w-16 hairline-strong h-px inline-block" />
            <span className="overline">Six Serves</span>
          </div>
          <h1 className="display-tight text-5xl sm:text-6xl md:text-7xl max-w-4xl">
            Mix the islands.
            <br />
            <span className="italic font-light text-[#DCCFB4]">Share the aloha.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-[#DCCFB4]/80 mt-8 max-w-2xl leading-[1.6]">
            Six signature serves built with POG Vodka and POG Spiked Island Punch.
            Every recipe has its own page — mix it, share it, tag it.
          </p>
        </motion.div>

        <div className="space-y-4 mb-16 border-y border-[#EFE7D6]/10 py-8">
          <FilterRow label="Base" options={COCKTAIL_FILTERS.base} active={base} onChange={setBase} group="base" />
          <FilterRow label="Level" options={COCKTAIL_FILTERS.difficulty} active={difficulty} onChange={setDifficulty} group="difficulty" />
          <FilterRow label="Occasion" options={COCKTAIL_FILTERS.occasion} active={occasion} onChange={setOccasion} group="occasion" />
        </div>

        <p className="overline text-[10px] text-[#EFE7D6]/40 mb-10" data-testid="gallery-count">
          {filtered.length} {filtered.length === 1 ? "recipe" : "recipes"}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-10">
          {filtered.map((c, i) => (
            <motion.article
              key={c.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`cocktail-card-${c.slug}`}
            >
              <Link to={`/cocktails/${c.slug}`} className="group block">
                <div
                  className="relative aspect-[3/4] overflow-hidden mb-6"
                  style={{ background: "radial-gradient(ellipse at 50% 45%, #2a1f18 0%, #0f0a07 80%)" }}
                >
                  <div className="absolute inset-0 opacity-20 sunburst-ghost pointer-events-none" />
                  <img
                    src={c.image}
                    alt={`${c.name} — POG Spirits cocktail recipe`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain p-10 transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 right-4 text-[#F26B1F] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight size={22} />
                  </span>
                </div>
                <div className="overline text-[10px] mb-2 text-[#EFE7D6]/50">
                  {String(i + 1).padStart(2, "0")} · {c.base} · {c.difficulty}
                </div>
                <h2 className="font-display text-3xl mb-2 group-hover:text-[#F26B1F] transition-colors duration-300">
                  {c.name}
                </h2>
                <p className="font-body text-[#DCCFB4]/70 text-sm leading-relaxed">{c.tagline}</p>
              </Link>
            </motion.article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-24" data-testid="gallery-empty">
            <p className="font-display text-3xl mb-4">No pours match that mix.</p>
            <p className="font-body text-[#DCCFB4]/70">Loosen a filter or two and try again.</p>
          </div>
        )}
      </main>

      <footer className="border-t border-[#EFE7D6]/10 px-8 py-10 text-center">
        <p className="text-xs text-[#EFE7D6]/40 tracking-wider">
          POG Spirits · Please enjoy responsibly · 21+
        </p>
      </footer>
    </div>
  );
};

export default CocktailsGallery;
